import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap text-sm font-bold uppercase tracking-tight transition-all duration-[140ms] focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-primary/10 focus-visible:ring-offset-0 disabled:pointer-events-none disabled:opacity-50 cursor-pointer [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 active:scale-[0.97]",
  {
    variants: {
      variant: {
        // Primary Button — burgundy, lift hover
        default: "bg-primary text-primary-foreground border border-primary hover:bg-primary/90 hover:-translate-y-px",

        // Destructive Button — red, lift hover
        destructive:
          "bg-destructive text-destructive-foreground border border-destructive hover:bg-destructive/90 hover:-translate-y-px",

        // Outline Button — transparent, subtle lift
        outline:
          "border border-border bg-transparent text-foreground hover:bg-primary/[0.04] hover:border-primary hover:-translate-y-px",

        // Secondary Button — surface color
        secondary:
          "bg-secondary text-secondary-foreground border border-border hover:bg-secondary/80 hover:-translate-y-px",

        // Tertiary Button — minimal
        tertiary:
          "border border-muted-foreground bg-transparent text-muted-foreground hover:border-foreground hover:text-foreground",

        // Ghost — invisible border
        ghost: "hover:bg-muted hover:text-foreground border border-transparent",

        // Link style
        link: "text-primary underline-offset-4 hover:underline border-0 normal-case font-medium active:scale-100",

        // Premium — accent with wider tracking
        premium: "bg-primary text-primary-foreground border border-primary font-bold uppercase text-xs tracking-wider hover:-translate-y-px",
      },
      size: {
        default: "h-10 px-4 py-2",
        sm: "h-9 px-3 text-xs",
        lg: "h-12 px-8 text-base",
        icon: "h-10 w-10",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, style, ...props }, ref) => {
    const Comp = asChild ? Slot : "button"

    // Add warm shadow for solid buttons
    const resolvedVariant = variant ?? "default";
    const shouldHaveShadow = resolvedVariant === 'default' || resolvedVariant === 'destructive' || resolvedVariant === 'secondary' || resolvedVariant === 'outline' || resolvedVariant === 'premium';
    const warmShadow = shouldHaveShadow ? {
      boxShadow: 'var(--shadow-warm-sm)',
    } : {};

    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        style={{ ...warmShadow, ...style }}
        ref={ref}
        {...props}
      />
    )
  }
)
Button.displayName = "Button"

export { Button, buttonVariants }
